import { Menu as BaseMenu } from "@base-ui/react/menu";
import type * as React from "react";
import type { ComponentProps, ReactNode } from "react";

type Styled<T extends React.ElementType> = Omit<ComponentProps<T>, "className"> & { className?: string | undefined };
import { cx } from "./cx.js";

/**
 * Styled Base UI Menu. Compose as:
 *   <Menu.Root><Menu.Trigger/><Menu.Content><Menu.Item/>…</Menu.Content></Menu.Root>
 */
function Content({
  children,
  className,
  align = "start",
  side = "bottom",
  sideOffset = 6,
  ...props
}: Styled<typeof BaseMenu.Popup> & { align?: "start" | "center" | "end"; side?: "top" | "bottom" | "left" | "right"; sideOffset?: number }) {
  return (
    <BaseMenu.Portal>
      <BaseMenu.Positioner className="dt-popover-positioner" align={align} side={side} sideOffset={sideOffset}>
        <BaseMenu.Popup className={cx("dt-popover", "dt-menu", className)} {...props}>
          {children}
        </BaseMenu.Popup>
      </BaseMenu.Positioner>
    </BaseMenu.Portal>
  );
}

function Item({ className, icon, trailing, children, ...props }: Styled<typeof BaseMenu.Item> & { icon?: ReactNode; trailing?: ReactNode }) {
  return (
    <BaseMenu.Item className={cx("dt-menu__item", className)} {...props}>
      {icon && <span className="dt-menu__icon">{icon}</span>}
      <span className="dt-menu__label">{children}</span>
      {trailing && <span className="dt-menu__icon">{trailing}</span>}
    </BaseMenu.Item>
  );
}

function LinkItem({ className, icon, children, ...props }: Styled<typeof BaseMenu.LinkItem> & { icon?: ReactNode }) {
  return (
    <BaseMenu.LinkItem className={cx("dt-menu__item", className)} closeOnClick {...props}>
      {icon && <span className="dt-menu__icon">{icon}</span>}
      <span className="dt-menu__label">{children}</span>
    </BaseMenu.LinkItem>
  );
}

function RadioItem({ className, children, ...props }: Styled<typeof BaseMenu.RadioItem>) {
  return (
    <BaseMenu.RadioItem className={cx("dt-menu__item", "dt-menu__item--radio", className)} {...props}>
      <BaseMenu.RadioItemIndicator className="dt-menu__radio" keepMounted>
        <span />
      </BaseMenu.RadioItemIndicator>
      <span className="dt-menu__label">{children}</span>
    </BaseMenu.RadioItem>
  );
}

function GroupLabel({ className, ...props }: Styled<typeof BaseMenu.GroupLabel>) {
  return <BaseMenu.GroupLabel className={cx("dt-menu__group-label", className)} {...props} />;
}

function Separator({ className, ...props }: Styled<typeof BaseMenu.Separator>) {
  return <BaseMenu.Separator className={cx("dt-menu__separator", className)} {...props} />;
}

export const Menu = {
  Root: BaseMenu.Root,
  Trigger: BaseMenu.Trigger,
  Group: BaseMenu.Group,
  RadioGroup: BaseMenu.RadioGroup,
  Content,
  Item,
  LinkItem,
  RadioItem,
  GroupLabel,
  Separator,
};
